import { useEffect, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Shield, Receipt, Clock, CheckCircle2, Layers, FileSpreadsheet, BarChart3, History, RefreshCw, Search, FileText, Users, Wallet as WalletIcon, AlertTriangle, ArrowDownRight, ArrowUpRight, Send } from "lucide-react";

// Admin oversight: trade-credit insurance applications + claims platform-wide.
// Admin can approve/deny claims (paid status credits seller wallet automatically
// via the backend endpoint).
export default function AdminInsurance() {
  const [tab, setTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [claims, setClaims] = useState([]);
  const load = () => Promise.all([
    api.get("/admin/insurance/applications"),
    api.get("/admin/insurance/claims"),
  ]).then(([a, c]) => { setApps(a.data); setClaims(c.data); });
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/admin/insurance/claims/${id}?status=${status}`);
      toast.success(`Claim ${status}${status === "paid" ? " · seller wallet credited" : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update");
    }
  };

  const totalCovered = claims.filter(c => c.status === "paid").reduce((s, c) => s + (c.covered_amount || 0), 0);
  const pendingClaims = claims.filter(c => c.status === "submitted" || c.status === "reviewing");

  return (
    <>
      <PageHeader title="Insurance · Oversight"
        subtitle={`${apps.length} applications · ${claims.length} claims · ${money(totalCovered)} paid out`}/>
      <div className="p-8 space-y-6">
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { l: "Active policies", v: apps.filter(a => a.status === "active").length, icon: Shield },
            { l: "Pending applications", v: apps.filter(a => a.status === "submitted" || a.status === "reviewing").length, icon: Clock },
            { l: "Open claims", v: pendingClaims.length, icon: Receipt },
            { l: "Total paid out", v: money(totalCovered), icon: CheckCircle2 },
          ].map((t, i) => (
            <div key={i} className="rounded-2xl bg-white border border-border p-5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">{t.l}</div>
                <t.icon className="w-3.5 h-3.5 text-foreground/40"/>
              </div>
              <div className="display text-2xl mt-1.5">{t.v}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-1.5 flex-wrap" data-testid="ins-admin-tabs">
          {[
            { k: "apps", l: "Applications", c: apps.length },
            { k: "claims", l: "Claims", c: claims.length },
            { k: "covers", l: "Covers", c: null },
            { k: "policies", l: "Policies", c: null },
            { k: "concentration", l: "Concentration", c: null },
            { k: "allianz-wallet", l: "Allianz wallet", c: null },
            { k: "takeover-queue", l: "Takeover queue", c: null },
            { k: "collections", l: "Collections", c: null },
            { k: "forecast", l: "Forecast", c: null },
            { k: "state-licenses", l: "State licenses", c: null },
            { k: "pending-topups", l: "Pending topups", c: null },
            { k: "audit", l: "Audit log", c: null },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ins-tab-${t.k}`}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm border ${tab === t.k ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/75 border-border hover:border-foreground/30"}`}>
              {t.l}
              {t.c !== null && <span className={`text-[10px] mono ${tab === t.k ? "text-background/70" : "text-foreground/45"}`}>{t.c}</span>}
            </button>
          ))}
        </div>

        {tab === "apps" && (
          <div className="rounded-2xl bg-white border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
                <tr><th className="px-5 py-3 font-medium">Seller</th><th className="px-5 py-3 font-medium">Coverage limit</th><th className="px-5 py-3 font-medium">Monthly premium</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium">Policy #</th><th className="px-5 py-3 font-medium">Submitted</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {apps.map(a => (
                  <tr key={a.id}>
                    <td className="px-5 py-3 font-medium">{a.seller_name}</td>
                    <td className="px-5 py-3 display">{money(a.coverage_limit)}</td>
                    <td className="px-5 py-3 mono">{money(a.monthly_premium)}</td>
                    <td className="px-5 py-3"><span className={`chip border ${a.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{a.status}</span></td>
                    <td className="px-5 py-3 mono text-xs">{a.policy_number || "—"}</td>
                    <td className="px-5 py-3 text-xs text-foreground/55">{new Date(a.submitted_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {apps.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-foreground/55">No applications yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "claims" && (
          <div className="rounded-2xl bg-white border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
                <tr><th className="px-5 py-3 font-medium">Invoice</th><th className="px-5 py-3 font-medium">Buyer</th><th className="px-5 py-3 font-medium">Claimed</th><th className="px-5 py-3 font-medium">Covered (90%)</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium">Filed</th><th/></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {claims.map(c => (
                  <tr key={c.id} data-testid={`adm-claim-${c.id.slice(0, 6)}`}>
                    <td className="px-5 py-3 mono text-xs">{c.invoice_code}</td>
                    <td className="px-5 py-3 font-medium">{c.buyer_name}</td>
                    <td className="px-5 py-3 display">{money(c.claimed_amount)}</td>
                    <td className="px-5 py-3 text-emerald-700 display">{money(c.covered_amount)}</td>
                    <td className="px-5 py-3"><span className={`chip border ${c.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : c.status === "denied" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status}</span></td>
                    <td className="px-5 py-3 text-xs text-foreground/55">{new Date(c.filed_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right space-x-1">
                      {(c.status === "submitted" || c.status === "reviewing") && (
                        <>
                          <Button size="sm" onClick={() => decide(c.id, "paid")} data-testid={`approve-${c.id.slice(0, 6)}`}
                            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-8 px-3 text-xs">Approve & pay</Button>
                          <Button size="sm" variant="outline" onClick={() => decide(c.id, "denied")}
                            className="rounded-full h-8 px-3 text-xs">Deny</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {claims.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-foreground/55">No claims filed yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {tab === "covers"        && <CoversTab/>}
        {tab === "policies"      && <PoliciesTab/>}
        {tab === "concentration" && <ConcentrationTab/>}
        {tab === "allianz-wallet"  && <AllianzWalletTab/>}
        {tab === "takeover-queue" && <TakeoverQueueTab/>}
        {tab === "collections"    && <CollectionsTab/>}
        {tab === "forecast"       && <ForecastTab/>}
        {tab === "state-licenses" && <StateLicensesTab/>}
        {tab === "pending-topups" && <PendingTopupsTab/>}
        {tab === "audit"         && <AuditTab/>}
      </div>
    </>
  );
}


// ─── Covers tab — cross-seller buyer covers roster ──────────────────────
function CoversTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(365);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/buyer-covers/list?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load covers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sinceDays]);

  return (
    <div className="space-y-4" data-testid="ins-covers">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total covers" value={data?.kpis?.total ?? 0} icon={Layers}/>
        <Kpi label="Active" value={data?.kpis?.active_count ?? 0} icon={CheckCircle2} tone="emerald"/>
        <Kpi label="Total limit" value={money(data?.kpis?.total_limit ?? 0)} icon={Shield}/>
        <Kpi label="Window" value={`${sinceDays}d`} icon={Clock}/>
      </div>
      <FilterBar
        q={q} setQ={setQ} onSearch={load}
        placeholder="Buyer/seller email or company"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="cov-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="accepted">Accepted</option>
            <option value="pending">Pending</option>
            <option value="refused">Refused</option>
            <option value="revoked">Revoked</option>
            <option value="investigation">Investigation</option>
          </select>
        }
        counts={data?.by_status} total={data?.total}
      />
      <DataTable
        loading={loading} rows={data?.rows || []} testid="cov"
        columns={[
          { l: "Seller",  r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Limit",   r: (x) => <span className="display tabular-nums">{money(x.permanent_limit)}</span> },
          { l: "Status",  r: (x) => <Pill v={x.status}/> },
          { l: "Mode",    r: (x) => <span className="text-[10.5px] mono uppercase text-foreground/55">{x.is_mock ? "MOCK" : "LIVE"}</span> },
          { l: "Updated", r: (x) => <span className="text-[11px] text-foreground/55">{x.days_in_status != null ? `${x.days_in_status}d ago` : "—"}</span> },
        ]}
        empty="No covers in this window."
      />
    </div>
  );
}


// ─── Policies tab — per-seller Allianz config ───────────────────────────
function PoliciesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configuredOnly, setConfiguredOnly] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (configuredOnly) p.set("configured_only", "true");
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/insurance/policies?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load policies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [configuredOnly]);

  return (
    <div className="space-y-4" data-testid="ins-policies">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total sellers" value={data?.kpis?.total_sellers ?? 0} icon={Shield}/>
        <Kpi label="Configured"    value={data?.kpis?.configured ?? 0} icon={CheckCircle2} tone="emerald"/>
        <Kpi label="Unconfigured"  value={data?.kpis?.unconfigured ?? 0} icon={Clock} tone="amber"/>
        <Kpi label="Webhook on"    value={data?.kpis?.webhook_on ?? 0} icon={Layers}/>
      </div>
      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input data-testid="pol-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Seller email or company" className="h-9 pl-8 text-[13px] w-[260px]"/>
        </div>
        <label className="inline-flex items-center gap-1.5 text-[12.5px]">
          <input data-testid="pol-configured-only" type="checkbox" checked={configuredOnly} onChange={(e) => setConfiguredOnly(e.target.checked)}/>
          Configured only
        </label>
        <button data-testid="pol-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">{data?.total ?? 0} rows</div>
      </div>
      <DataTable
        loading={loading} rows={data?.rows || []} testid="pol"
        columns={[
          { l: "Seller",      r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Tenant",      r: (x) => <span className="mono text-[11px] text-foreground/65">{x.tenant_slug || "—"}</span> },
          { l: "Policy ID",   r: (x) => <span className="mono text-[11px]">{x.policy_id || "—"}</span> },
          { l: "BU",          r: (x) => <span className="text-[11px]">{x.business_unit_code || "—"}</span> },
          { l: "Webhook",     r: (x) => (
            <span className={`chip border text-[10.5px] ${x.webhook_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
              {x.webhook_enabled ? "ON" : "OFF"}
            </span>
          )},
          { l: "Configured",  r: (x) => <Pill v={x.configured ? "configured" : "unconfigured"}/> },
          { l: "Saved",       r: (x) => <span className="text-[11px] text-foreground/55">{x.saved_at ? new Date(x.saved_at).toLocaleDateString() : "—"}</span> },
        ]}
        empty="No tenants found."
      />
    </div>
  );
}


// ─── Concentration tab — top concentrated buyers platform-wide ──────────
function ConcentrationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minLimit, setMinLimit] = useState(10000);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/insurance/concentration?min_limit_usd=${minLimit}&limit=50`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load concentration"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [minLimit]);

  return (
    <div className="space-y-4" data-testid="ins-concentration">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Concentrated buyers" value={data?.total ?? 0} icon={BarChart3}/>
        <Kpi label="Aggregate exposure"  value={money(data?.total_exposure ?? 0)} icon={Shield} tone="amber"/>
        <Kpi label="Min limit filter"    value={money(minLimit)} icon={Layers}/>
      </div>
      <div className="flex items-center gap-2 bg-white border border-border rounded-xl p-3">
        <span className="text-[12.5px] text-foreground/65">Show buyers with aggregate limit ≥</span>
        <select data-testid="conc-min" value={String(minLimit)} onChange={(e) => setMinLimit(Number(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          <option value="0">$0</option>
          <option value="10000">$10K</option>
          <option value="50000">$50K</option>
          <option value="100000">$100K</option>
          <option value="500000">$500K</option>
          <option value="1000000">$1M</option>
        </select>
        <button data-testid="conc-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
      </div>
      <DataTable
        loading={loading} rows={data?.rows || []} testid="conc"
        columns={[
          { l: "Rank",          r: (_, i) => <span className="text-[11px] text-foreground/55">#{i + 1}</span> },
          { l: "Buyer",         r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Aggregate limit", r: (x) => <span className="display tabular-nums">{money(x.total_limit)}</span> },
          { l: "Seller count",  r: (x) => (
            <span className={`chip border text-[10.5px] ${x.seller_count >= 5 ? "bg-rose-50 text-rose-700 border-rose-200" : x.seller_count >= 3 ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
              {x.seller_count} sellers
            </span>
          )},
        ]}
        empty="No concentrated buyers above the threshold."
      />
    </div>
  );
}


// ─── Audit tab — quotes log + billing runs log + buyer-purchased policies ──
function AuditTab() {
  const [view, setView] = useState("quotes");
  return (
    <div className="space-y-4" data-testid="ins-audit">
      <div className="inline-flex items-center gap-1 rounded-full bg-secondary/40 border border-border p-1">
        {[
          { k: "quotes",         l: "Quotes",         i: FileSpreadsheet },
          { k: "billing",        l: "Billing runs",   i: History },
          { k: "buyer-policies", l: "Buyer policies", i: FileText },
        ].map((t) => {
          const Icon = t.i;
          const active = view === t.k;
          return (
            <button key={t.k} data-testid={`audit-sub-${t.k}`} onClick={() => setView(t.k)}
              className={`inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-full transition-colors ${
                active ? "bg-white border border-border shadow-sm text-foreground" : "text-foreground/60 hover:text-foreground"
              }`}>
              <Icon size={13}/>{t.l}
            </button>
          );
        })}
      </div>
      {view === "quotes"         && <QuotesView/>}
      {view === "billing"        && <BillingRunsView/>}
      {view === "buyer-policies" && <BuyerPoliciesView/>}
    </div>
  );
}


function QuotesView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/insurance/quotes-log?since_days=${sinceDays}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load quotes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays]);

  return (
    <div className="space-y-3">
      <FilterBar q="" setQ={() => {}} hideSearch onSearch={load} sinceDays={sinceDays} setSinceDays={setSinceDays}
        counts={data?.by_status} total={data?.total}/>
      {data?.by_grade && Object.keys(data.by_grade).length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="quotes-by-grade">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">Quotes by grade</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.by_grade).map(([g, n]) => (
              <span key={g} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/60 border border-border text-[11px]">
                <span className="font-medium">{g}</span><span className="text-foreground/55">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <DataTable
        loading={loading} rows={data?.rows || []} testid="qte"
        columns={[
          { l: "Seller",   r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Buyer",    r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Requested", r: (x) => <span className="display tabular-nums">{money(x.requested_limit)}</span> },
          { l: "Grade",    r: (x) => <span className="mono text-[11px]">{x.grade || "—"}</span> },
          { l: "Status",   r: (x) => <Pill v={x.status}/> },
          { l: "Quoted",   r: (x) => <span className="text-[11px] text-foreground/55">{x.quoted_at ? new Date(x.quoted_at).toLocaleString() : "—"}</span> },
        ]}
        empty="No quote attempts in this window."
      />
    </div>
  );
}


function BillingRunsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(180);
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      const r = await api.get(`/admin/insurance/billing-runs?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load billing runs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, status]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Total runs"    value={data?.kpis?.total_runs ?? 0} icon={History}/>
        <Kpi label="Total debited" value={money(data?.kpis?.total_debited ?? 0)} icon={Shield} tone="emerald"/>
        <Kpi label="Window"        value={`${sinceDays}d`} icon={Clock}/>
      </div>
      <FilterBar q="" setQ={() => {}} hideSearch onSearch={load} sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="br-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All</option>
            <option value="ok">OK</option>
            <option value="failed">Failed</option>
            <option value="idempotent_skip">Idempotent skip</option>
          </select>
        }
        counts={data?.by_status} total={data?.total}/>
      <DataTable
        loading={loading} rows={data?.rows || []} testid="brun"
        columns={[
          { l: "Seller", r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Month",  r: (x) => <span className="mono text-[11px]">{x.month || "—"}</span> },
          { l: "Amount", r: (x) => <span className="display tabular-nums">{money(x.amount)}</span> },
          { l: "Covers", r: (x) => <span className="text-[11px]">{x.covers_count ?? "—"}</span> },
          { l: "Status", r: (x) => <Pill v={x.status}/> },
          { l: "Run at", r: (x) => <span className="text-[11px] text-foreground/55">{x.run_at ? new Date(x.run_at).toLocaleString() : "—"}</span> },
        ]}
        empty="No billing runs in this window."
      />
    </div>
  );
}


// ─── Buyer policies view — buyer-purchased per-invoice insurance ────────
function BuyerPoliciesView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(365);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/insurance/buyer-policies/list?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load buyer policies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, status]);

  const avgRatePct = ((data?.kpis?.avg_premium_rate ?? 0) * 100).toFixed(3);

  return (
    <div className="space-y-3" data-testid="ins-buyer-policies">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total policies"    value={data?.kpis?.total_count ?? 0} icon={FileText}/>
        <Kpi label="Premium collected" value={money(data?.kpis?.total_premium_usd ?? 0)} icon={Receipt} tone="emerald"/>
        <Kpi label="Total covered"     value={money(data?.kpis?.total_covered_usd ?? 0)} icon={Shield}/>
        <Kpi label="Avg rate"          value={`${avgRatePct}%`} icon={BarChart3} tone="amber"/>
      </div>
      <FilterBar
        q={q} setQ={setQ} onSearch={load}
        placeholder="Invoice code, buyer or seller"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="bp-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="claimed">Claimed</option>
            <option value="expired">Expired</option>
            <option value="pending">Pending</option>
          </select>
        }
        counts={data?.by_status} total={data?.total}
      />
      {data?.top_buyers && data.top_buyers.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="bp-top-buyers">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2 inline-flex items-center gap-1.5">
            <Users size={11}/>Top 10 buyers by premium paid · fraud signal
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {data.top_buyers.map((b, i) => (
              <div key={b.buyer_id || i} data-testid={`bp-top-${i}`}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-secondary/40 border border-border">
                <span className="text-[10px] mono text-foreground/55">#{i + 1}</span>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-[12px] font-medium truncate">{b.buyer_company || b.buyer_email || "—"}</div>
                  <div className="text-[10.5px] text-foreground/55 truncate">{b.buyer_email || ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="display text-[12.5px] tabular-nums">{money(b.total_premium)}</div>
                  <div className="text-[10px] text-foreground/55">{b.policy_count} policies</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <DataTable
        loading={loading} rows={data?.rows || []} testid="bp"
        columns={[
          { l: "Invoice", r: (x) => <span className="mono text-[11px]">{x.invoice_code || "—"}</span> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Seller",  r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Amount",  r: (x) => <span className="display tabular-nums">{money(x.amount)}</span> },
          { l: "Premium", r: (x) => <span className="display tabular-nums text-emerald-700">{money(x.premium)}</span> },
          { l: "Rate",    r: (x) => <span className="mono text-[11px]">{((x.effective_rate || 0) * 100).toFixed(3)}%</span> },
          { l: "Cov %",   r: (x) => <span className="mono text-[11px]">{Math.round((x.coverage_pct || 0) * 100)}%</span> },
          { l: "Status",  r: (x) => <Pill v={x.status}/> },
          { l: "Created", r: (x) => <span className="text-[11px] text-foreground/55">{x.created_at ? new Date(x.created_at).toLocaleDateString() : "—"}</span> },
        ]}
        empty="No buyer-purchased policies in this window."
      />
    </div>
  );
}


// ─── Shared sub-components ──────────────────────────────────────────────
function Kpi({ label, value, icon: Icon, tone }) {
  const tones = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" };
  return (
    <div className="rounded-2xl bg-white border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/40"/>}
      </div>
      <div className={`display text-2xl mt-1.5 ${tones[tone] || ""}`}>{value}</div>
    </div>
  );
}

function FilterBar({ q, setQ, onSearch, placeholder, sinceDays, setSinceDays, extra, counts, total, hideSearch }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
      {!hideSearch && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input data-testid="ins-filter-q" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
            placeholder={placeholder} className="h-9 pl-8 text-[13px] w-[260px]"/>
        </div>
      )}
      {extra}
      <select data-testid="ins-filter-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
        className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="180">Last 6 months</option>
        <option value="365">Last 1 year</option>
        <option value="730">Last 2 years</option>
      </select>
      <button data-testid="ins-filter-refresh" onClick={onSearch}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
        <RefreshCw size={13}/> Refresh
      </button>
      <div className="ml-auto flex items-center gap-3 text-[11px] text-foreground/55">
        <span>{total ?? 0} rows</span>
        {counts && Object.keys(counts).length > 0 && (
          <span className="flex items-center gap-1.5">
            {Object.entries(counts).slice(0, 4).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/60 border border-border">
                <span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium">{v}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// ALLIANZ WALLET TAB (iter-93)
// ════════════════════════════════════════════════════════════════════════
function AllianzWalletTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceDays, setSinceDays] = useState(30);
  const [txnType, setTxnType] = useState("");
  const [busy, setBusy] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (txnType) p.set("txn_type", txnType);
      const r = await api.get(`/admin/insurance/wallet?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load Allianz wallet"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sinceDays, txnType]);

  return (
    <div className="space-y-4" data-testid="ins-allianz-wallet">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Balance" value={money(data?.balance ?? 0)} icon={WalletIcon} tone="emerald"/>
        <Kpi label="Inflow · 30d" value={money(data?.kpis_30d?.inflow_usd ?? 0)} icon={ArrowDownRight} tone="emerald"/>
        <Kpi label="Outflow · 30d" value={money(data?.kpis_30d?.outflow_usd ?? 0)} icon={ArrowUpRight} tone="rose"/>
        <Kpi label="Open collections" value={data?.outstanding_collections?.open_count ?? 0} icon={FileText}/>
        <Kpi label="Outstanding owed" value={money(data?.outstanding_collections?.balance_owed_total_usd ?? 0)} icon={AlertTriangle} tone="amber"/>
      </div>
      <FilterBar
        q="" setQ={() => {}} hideSearch onSearch={load}
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="aw-type" value={txnType} onChange={(e) => setTxnType(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All types</option>
              <option value="allianz_topup">Topup</option>
              <option value="allianz_withdraw">Withdraw</option>
              <option value="allianz_takeover_payout">Takeover payout</option>
              <option value="allianz_clearing_topup">Clearing top-up</option>
            </select>
            <Button data-testid="aw-topup-btn" size="sm" className="h-9 rounded-md"
              onClick={() => setTopupOpen(true)}>
              <ArrowDownRight className="w-3.5 h-3.5 mr-1"/>Top up
            </Button>
            <Button data-testid="aw-withdraw-btn" size="sm" variant="outline" className="h-9 rounded-md"
              onClick={() => setWithdrawOpen(true)}>
              <ArrowUpRight className="w-3.5 h-3.5 mr-1"/>Withdraw
            </Button>
          </>
        }
        counts={data?.by_type ? Object.fromEntries(Object.entries(data.by_type).map(([k, v]) => [k, v.n])) : null}
        total={data?.transactions?.length ?? 0}
      />
      <DataTable loading={loading} rows={data?.transactions || []} testid="aw"
        empty="No Allianz wallet activity in this window."
        columns={[
          { l: "When", r: (x) => <span className="text-[11px] text-foreground/55">{new Date(x.created_at).toLocaleString()}</span> },
          { l: "Type", r: (x) => <span className="text-[11px] mono">{x.type}</span> },
          { l: "Amount", r: (x) => <span className={`display text-[14px] tabular-nums ${x.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {x.amount >= 0 ? "+" : ""}{money(x.amount)}</span> },
          { l: "Status", r: (x) => <Pill v={x.status}/> },
          { l: "Method", r: (x) => <span className="text-[11px] text-foreground/55">{x.method || "—"}</span> },
          { l: "Loan #", r: (x) => x.metadata?.loan_number ? <span className="mono text-[10.5px]">{x.metadata.loan_number}</span> : <span className="text-[11px] text-foreground/40">—</span> },
          { l: "Reference", r: (x) => <span className="mono text-[10.5px] text-foreground/55">{x.ref || "—"}</span> },
        ]}/>
      <AllianzMoneyDialog open={topupOpen} mode="topup" onClose={() => setTopupOpen(false)} onDone={load} busy={busy} setBusy={setBusy}/>
      <AllianzMoneyDialog open={withdrawOpen} mode="withdraw" onClose={() => setWithdrawOpen(false)} onDone={load} busy={busy} setBusy={setBusy}/>
    </div>
  );
}

function AllianzMoneyDialog({ open, mode, onClose, onDone, busy, setBusy }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("wire");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  if (!open) return null;
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if ((reason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    setBusy(true);
    try {
      const path = mode === "topup"
        ? "/admin/insurance/wallet/topup"
        : "/admin/insurance/wallet/withdraw";
      await api.post(path, { amount: amt, method,
                              reference: reference || undefined, reason });
      toast.success(`${mode === "topup" ? "Topped up" : "Withdrew"} ${money(amt)}`);
      setAmount(""); setReference(""); setReason("");
      onClose(); onDone();
    } catch (e) {
      const err = e.response?.data?.detail || e.response?.data;
      toast.error(typeof err === "string" ? err : (err?.reason || `${mode} failed`));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={`aw-${mode}-dialog`}>
      <div className="bg-white rounded-2xl border border-border p-6 w-full max-w-md space-y-3">
        <h3 className="display text-xl">{mode === "topup" ? "Top up Allianz wallet" : "Withdraw from Allianz wallet"}</h3>
        <p className="text-[12.5px] text-foreground/65">{mode === "topup"
          ? "Pre-fund the clearing wallet on Allianz's behalf to cover upcoming takeover payouts."
          : "Pull funds from the clearing wallet back to Allianz's external bank."}</p>
        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-wider text-foreground/55">Amount (USD)</label>
          <Input data-testid={`aw-${mode}-amount`} type="number" value={amount}
            onChange={(e) => setAmount(e.target.value)} placeholder="100000"/>
        </div>
        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-wider text-foreground/55">Method</label>
          <select data-testid={`aw-${mode}-method`} value={method} onChange={(e) => setMethod(e.target.value)}
            className="h-9 text-[12.5px] w-full rounded-md border border-border bg-white px-2">
            <option value="wire">Wire</option>
            <option value="ach">ACH</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reference (optional)</label>
          <Input data-testid={`aw-${mode}-ref`} value={reference}
            onChange={(e) => setReference(e.target.value)} placeholder="ALZ-2026-Q1-FUND"/>
        </div>
        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reason (audit, ≥3 chars)</label>
          <Input data-testid={`aw-${mode}-reason`} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={mode === "topup" ? "Pre-funding for Q1 takeover load" : "Quarterly excess sweep"}/>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid={`aw-${mode}-submit`} onClick={submit} disabled={busy}>
            <Send className="w-3.5 h-3.5 mr-1"/>{busy ? "Submitting…" : (mode === "topup" ? "Top up" : "Withdraw")}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// TAKEOVER QUEUE TAB
// ════════════════════════════════════════════════════════════════════════
function TakeoverQueueTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(55);
  const [funder, setFunder] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ threshold_days: String(threshold) });
      if (funder) p.set("funder_type", funder);
      const r = await api.get(`/admin/insurance/takeover-queue?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load takeover queue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold, funder]);

  const approve = async (loan) => {
    if (!window.confirm(`Approve takeover for ${loan.loan_number}? Pays ${money(loan.total_owed)} to the funder.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/loans/${loan.id}/force-allianz-takeover`);
      toast.success(`Takeover approved · ${loan.loan_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const submitReject = async () => {
    if ((rejectReason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/loans/${rejectFor.id}/reject-allianz-takeover`, { reason: rejectReason });
      toast.success(`Refused coverage · ${rejectFor.loan_number}`);
      setRejectFor(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ins-takeover-queue">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Loans in queue" value={data?.kpis?.total_count ?? 0} icon={AlertTriangle} tone="amber"/>
        <Kpi label="Total owed" value={money(data?.kpis?.total_owed_usd ?? 0)} icon={WalletIcon} tone="rose"/>
        <Kpi label="Threshold" value={`${threshold}d past due`} icon={Clock}/>
        <Kpi label="Review window" value={`${Math.max(0, 60 - threshold)}d before auto-fire`} icon={Shield} tone="emerald"/>
      </div>
      <FilterBar
        q="" setQ={() => {}} hideSearch onSearch={load}
        sinceDays={threshold} setSinceDays={setThreshold}
        extra={
          <select data-testid="tq-funder" value={funder} onChange={(e) => setFunder(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All funders</option>
            <option value="lender">Lender</option>
            <option value="seller">Seller</option>
          </select>
        }
        counts={data?.by_funder ? Object.fromEntries(Object.entries(data.by_funder).map(([k, v]) => [k, v.n])) : null}
        total={data?.total}
      />
      <DataTable loading={loading} rows={data?.rows || []} testid="tq"
        empty="Queue is clear — no loans at threshold."
        columns={[
          { l: "Loan #",  r: (x) => <span className="mono text-[11px]">{x.loan_number}</span> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Funder",  r: (x) => <span className={`chip border text-[10.5px] capitalize ${x.funder_type === "seller" ? "bg-sky-50 border-sky-200 text-sky-700" : "bg-violet-50 border-violet-200 text-violet-700"}`}>{x.funder_type || "lender"}{x.lender_org_name ? ` · ${x.lender_org_name}` : ""}</span> },
          { l: "Total owed", r: (x) => <span className="display text-[14px] tabular-nums">{money(x.total_owed)}</span> },
          { l: "DPD",     r: (x) => <span className="text-[11px] tabular-nums text-rose-700 font-medium">+{x.days_past_due}d</span> },
          { l: "Status",  r: (x) => <Pill v={x.status}/> },
          { l: "Review",  r: (x) => x.review_status
              ? <Pill v={x.review_status}/>
              : <span className="text-[11px] text-foreground/40">pending</span> },
          { l: "Action", r: (x) => (
            <div className="flex gap-1">
              <Button data-testid={`tq-approve-${x.id}`} size="sm" disabled={busy}
                onClick={() => approve(x)}
                className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]">
                <CheckCircle2 className="w-3 h-3 mr-0.5"/>Approve
              </Button>
              <Button data-testid={`tq-reject-${x.id}`} size="sm" variant="outline" disabled={busy}
                onClick={() => { setRejectFor(x); setRejectReason(""); }}
                className="h-7 text-rose-700 border-rose-200 text-[11px]">
                Refuse
              </Button>
            </div>
          )},
        ]}/>
      {rejectFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="tq-reject-dialog">
          <div className="bg-white rounded-2xl border border-border p-6 w-full max-w-md space-y-3">
            <h3 className="display text-xl">Refuse Allianz coverage</h3>
            <p className="text-[12.5px] text-foreground/65">
              Loan <span className="mono">{rejectFor.loan_number}</span> stays with funder.
              Total owed: <strong>{money(rejectFor.total_owed)}</strong>.
            </p>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reason (audit, ≥3 chars)</label>
              <Input data-testid="tq-reject-reason" value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Coverage exceeds aggregate buyer limit"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRejectFor(null)} disabled={busy}>Cancel</Button>
              <Button data-testid="tq-reject-submit" onClick={submitReject} disabled={busy}
                className="bg-rose-600 hover:bg-rose-700 text-white">
                {busy ? "Submitting…" : "Refuse coverage"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// COLLECTIONS TAB
// ════════════════════════════════════════════════════════════════════════
function CollectionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [funder, setFunder] = useState("");
  const [sinceDays, setSinceDays] = useState(365);
  const [q, setQ] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("wire");
  const [payRef, setPayRef] = useState("");
  const [payReason, setPayReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (funder) p.set("funder_type", funder);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/insurance/collections?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load collections"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, funder, sinceDays]);

  const submitPay = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if ((payReason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/insurance/collections/${payFor.id}/log-payment`, {
        amount: amt, method: payMethod,
        reference: payRef || undefined, reason: payReason,
      });
      toast.success(`Payment ${money(amt)} logged`);
      setPayFor(null); setPayAmount(""); setPayRef(""); setPayReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Log-payment failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ins-collections">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Collections" value={data?.kpis?.total_collections ?? 0} icon={FileText}/>
        <Kpi label="Outstanding" value={money(data?.kpis?.outstanding_balance_usd ?? 0)} icon={AlertTriangle} tone="rose"/>
        <Kpi label="Collected" value={money(data?.kpis?.collected_so_far_usd ?? 0)} icon={CheckCircle2} tone="emerald"/>
        <Kpi label="Original payouts" value={money(data?.kpis?.original_payouts_usd ?? 0)} icon={WalletIcon}/>
      </div>
      <FilterBar
        q={q} setQ={setQ} onSearch={load}
        placeholder="Loan # / buyer email / company"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <>
            <select data-testid="col-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
            <select data-testid="col-funder" value={funder} onChange={(e) => setFunder(e.target.value)}
              className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
              <option value="">All funders</option>
              <option value="lender">Lender</option>
              <option value="seller">Seller</option>
            </select>
          </>
        }
        counts={data?.by_status} total={data?.total}
      />
      <DataTable loading={loading} rows={data?.rows || []} testid="col"
        empty="No post-takeover collections in this window."
        columns={[
          { l: "Loan #",   r: (x) => <span className="mono text-[11px]">{x.loan_number}</span> },
          { l: "Buyer",    r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Funder",   r: (x) => <span className={`chip border text-[10.5px] capitalize ${x.funder_type === "seller" ? "bg-sky-50 border-sky-200 text-sky-700" : "bg-violet-50 border-violet-200 text-violet-700"}`}>{x.funder_type || "lender"}</span> },
          { l: "Original", r: (x) => <span className="tabular-nums">{money(x.original_payout)}</span> },
          { l: "Collected", r: (x) => <span className="tabular-nums text-emerald-700">{money(x.collected_usd)}</span> },
          { l: "Balance",  r: (x) => <span className="display text-[14px] tabular-nums text-rose-700">{money(x.balance_owed_usd)}</span> },
          { l: "Status",   r: (x) => <Pill v={x.status}/> },
          { l: "Opened",   r: (x) => <span className="text-[11px] text-foreground/55">{new Date(x.opened_at).toLocaleDateString()}</span> },
          { l: "Action",   r: (x) => x.status === "open"
              ? <Button data-testid={`col-log-${x.id}`} size="sm" className="h-7 text-[11px]"
                  onClick={() => { setPayFor(x); setPayAmount(""); setPayRef(""); setPayReason(""); }}>
                  <ArrowDownRight className="w-3 h-3 mr-0.5"/>Log payment
                </Button>
              : <span className="text-[11px] text-foreground/40">—</span> },
        ]}/>
      {payFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="col-pay-dialog">
          <div className="bg-white rounded-2xl border border-border p-6 w-full max-w-md space-y-3">
            <h3 className="display text-xl">Log buyer payment · {payFor.loan_number}</h3>
            <p className="text-[12.5px] text-foreground/65">
              Balance remaining: <strong>{money(payFor.balance_owed_usd)}</strong>
            </p>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Amount (USD)</label>
              <Input data-testid="col-pay-amount" type="number" value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)} placeholder="1000"/>
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Method</label>
              <select data-testid="col-pay-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                className="h-9 text-[12.5px] w-full rounded-md border border-border bg-white px-2">
                <option value="wire">Wire</option>
                <option value="ach">ACH</option>
                <option value="check">Check</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reference (optional)</label>
              <Input data-testid="col-pay-ref" value={payRef}
                onChange={(e) => setPayRef(e.target.value)} placeholder="WIRE-2026-04-14-A"/>
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reason (audit, ≥3 chars)</label>
              <Input data-testid="col-pay-reason" value={payReason}
                onChange={(e) => setPayReason(e.target.value)} placeholder="Buyer wired partial settlement"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPayFor(null)} disabled={busy}>Cancel</Button>
              <Button data-testid="col-pay-submit" onClick={submitPay} disabled={busy}>
                {busy ? "Logging…" : "Log payment"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function DataTable({ loading, rows, columns, empty, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid={`${testid}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
          <tr>{columns.map((c, i) => <th key={i} className="px-4 py-2.5 font-medium">{c.l}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55" data-testid={`${testid}-empty`}>{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id || `${i}`} data-testid={`${testid}-row-${i}`} className="hover:bg-secondary/20">
              {columns.map((c, j) => <td key={j} className="px-4 py-3">{c.r(r, i)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stack({ top, sub }) {
  return (
    <div className="leading-tight">
      <div className="text-[13px] font-medium">{top || "—"}</div>
      <div className="text-[11px] text-foreground/55">{sub || ""}</div>
    </div>
  );
}

function Pill({ v }) {
  const map = {
    accepted:        "bg-emerald-50 text-emerald-700 border-emerald-200",
    active:          "bg-emerald-50 text-emerald-700 border-emerald-200",
    quoted:          "bg-emerald-50 text-emerald-700 border-emerald-200",
    configured:      "bg-emerald-50 text-emerald-700 border-emerald-200",
    ok:              "bg-emerald-50 text-emerald-700 border-emerald-200",
    paid:            "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending:         "bg-amber-50 text-amber-800 border-amber-200",
    claimed:         "bg-amber-50 text-amber-800 border-amber-200",
    investigation:   "bg-amber-50 text-amber-800 border-amber-200",
    idempotent_skip: "bg-slate-100 text-slate-700 border-slate-200",
    unconfigured:    "bg-slate-100 text-slate-700 border-slate-200",
    expired:         "bg-slate-100 text-slate-700 border-slate-200",
    refused:         "bg-rose-50 text-rose-700 border-rose-200",
    revoked:         "bg-rose-50 text-rose-700 border-rose-200",
    failed:          "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${map[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {v || "—"}
    </span>
  );
}



// ════════════════════════════════════════════════════════════════════════
// FORECAST TAB (iter-98) — Allianz wallet runway + projected payouts
// ════════════════════════════════════════════════════════════════════════
function ForecastTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(14);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/insurance/forecast?runway_alert_days=${threshold}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load forecast"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threshold]);

  const runwayBadge = data?.runway_alert
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  const proj = data?.projections || {};

  return (
    <div className="space-y-4" data-testid="ins-forecast">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Wallet balance" value={money(data?.wallet_balance_usd ?? 0)} icon={WalletIcon} tone="emerald"/>
        <Kpi label="Avg daily burn · 90d" value={money(data?.avg_daily_burn_usd ?? 0)} icon={ArrowUpRight}/>
        <Kpi label="Runway · days"
              value={data?.runway_days === null ? "∞" : `${data?.runway_days ?? "—"}`}
              icon={Clock}
              tone={data?.runway_alert ? "rose" : "emerald"}/>
        <Kpi label="Open loans (insured)" value={data?.open_loan_count ?? 0} icon={Layers}/>
        <Kpi label="Alert" value={data?.runway_alert ? "On" : "Off"} icon={AlertTriangle} tone={data?.runway_alert ? "rose" : null}/>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[11px] uppercase tracking-wider text-foreground/55">Alert threshold (days)</label>
        <select data-testid="forecast-threshold" value={threshold}
          onChange={(e) => setThreshold(parseInt(e.target.value))}
          className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
          {[7, 14, 21, 30, 45, 60].map(d => <option key={d} value={d}>{d}d</option>)}
        </select>
        <span className={`chip border ${runwayBadge}`}>
          {data?.runway_alert ? "Runway low — top up Allianz wallet" : "Runway healthy"}
        </span>
        <Button data-testid="forecast-refresh" size="sm" variant="outline" className="h-9 rounded-md ml-auto" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-border p-5 space-y-3">
        <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50">Projected payouts</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[30, 60, 90, 120].map(w => (
            <div key={w} className="rounded-xl bg-secondary/40 border border-border p-3" data-testid={`forecast-window-${w}`}>
              <div className="text-[10px] mono text-foreground/55 uppercase">Next {w} days</div>
              <div className="display text-lg mt-1">{money(proj[w]?.total_usd ?? 0)}</div>
              <div className="text-[10.5px] text-foreground/55 mt-0.5">{proj[w]?.loan_count ?? 0} loans · principal {money(proj[w]?.principal_usd ?? 0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50">Daily timeline · next 120 days</div>
          <div className="text-[11px] text-foreground/55">{(data?.daily_timeline || []).length} bucket(s)</div>
        </div>
        <DataTable loading={loading} rows={(data?.daily_timeline || []).slice(0, 60)} testid="forecast-timeline"
          empty="No projected takeovers in the next 120 days."
          columns={[
            { l: "Takeover date", r: (x) => <span className="mono text-[11px]">{x.date}</span> },
            { l: "Loans", r: (x) => <span className="display text-[14px] tabular-nums">{x.loan_count}</span> },
            { l: "Projected amount", r: (x) => <span className="display text-[14px] tabular-nums text-rose-700">{money(x.amount_usd)}</span> },
          ]}/>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// STATE LICENSES TAB (iter-98) — per-org allowed_states roster + edit
// ════════════════════════════════════════════════════════════════════════
function StateLicensesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [statesText, setStatesText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/insurance/state-licenses`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load state licenses"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (org) => {
    setEditing(org.id);
    setStatesText((org.allianz_state_licenses || []).join(", "));
    setReason("");
  };

  const save = async () => {
    if ((reason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    const states = statesText.split(/[,\s]+/).map(s => s.trim().toUpperCase()).filter(s => s.length === 2);
    setBusy(true);
    try {
      await api.patch(`/admin/insurance/state-licenses/${editing}?reason=${encodeURIComponent(reason)}`, { allowed_states: states });
      toast.success(`Updated · ${states.length} state${states.length === 1 ? "" : "s"}`);
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ins-state-licenses">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Insurance orgs" value={data?.total ?? 0} icon={Shield}/>
        <Kpi label="Gate enabled" value={data?.gate_enabled ? "On" : "Off"} icon={AlertTriangle} tone={data?.gate_enabled ? "emerald" : null}/>
        <Kpi label="Unrestricted orgs" value={(data?.rows || []).filter(r => r.unrestricted).length} icon={CheckCircle2}/>
        <Kpi label="Restricted orgs" value={(data?.rows || []).filter(r => !r.unrestricted).length} icon={Layers}/>
      </div>
      <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-[12.5px] text-amber-900">
        <strong>Gate status:</strong> {data?.gate_enabled
          ? "ON · take-terms refuses lender-funded loans for buyers in non-licensed states."
          : "OFF · take-terms ignores state-licensing (set ALLIANZ_STATE_GATE_ENABLED=true in backend/.env to enforce)."}
      </div>
      <DataTable loading={loading} rows={data?.rows || []} testid="state-lic"
        empty="No insurance partner orgs configured."
        columns={[
          { l: "Org", r: (x) => <Stack top={x.name} sub={x.id?.slice(0, 8)}/> },
          { l: "Status", r: (x) => <Pill v={x.status}/> },
          { l: "State count", r: (x) => <span className="display text-[14px]">{x.state_count}</span> },
          { l: "Allowed states", r: (x) => <span className="text-[11px] text-foreground/65">{x.unrestricted ? <em className="text-emerald-700">unrestricted</em> : (x.allianz_state_licenses || []).join(", ")}</span> },
          { l: "", r: (x) => (
              <Button size="sm" variant="outline" data-testid={`state-lic-edit-${x.id?.slice(0, 6)}`}
                className="h-8 rounded-full text-[11px]"
                onClick={() => startEdit(x)}>Edit</Button>
            ) },
        ]}/>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="state-lic-dialog">
          <div className="bg-white rounded-2xl border border-border p-6 w-full max-w-md space-y-3">
            <h3 className="display text-xl">Update allowed states</h3>
            <p className="text-[12.5px] text-foreground/65">
              Enter 2-letter US state codes separated by commas. Leave empty to allow ALL states (unrestricted).
            </p>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Allowed states</label>
              <Input data-testid="state-lic-states" value={statesText}
                onChange={(e) => setStatesText(e.target.value)}
                placeholder="CA, NY, TX, FL"/>
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reason (audit, ≥3 chars)</label>
              <Input data-testid="state-lic-reason" value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Renewed CA + NY licenses Q2 2026"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
              <Button data-testid="state-lic-save" onClick={save} disabled={busy}>
                <Send className="w-3.5 h-3.5 mr-1"/>{busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// PENDING TOPUPS TAB (iter-98) — settle / fail topup state-machine
// ════════════════════════════════════════════════════════════════════════
function PendingTopupsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/insurance/wallet/pending-topups?limit=200`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load pending topups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const action = async () => {
    if (!dialog) return;
    if ((reason || "").trim().length < 3) { toast.error("Reason must be ≥ 3 chars"); return; }
    setBusyId(dialog.txn_id);
    try {
      const path = `/admin/insurance/wallet/topup/${dialog.txn_id}/${dialog.mode}`;
      await api.post(path, { reason });
      toast.success(`Topup ${dialog.mode === "settle" ? "settled & balance credited" : "marked failed"}`);
      setDialog(null); setReason(""); load();
    } catch (e) {
      const err = e.response?.data?.detail || e.response?.data;
      toast.error(typeof err === "string" ? err : (err?.reason || `${dialog.mode} failed`));
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="ins-pending-topups">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Pending count" value={data?.total ?? 0} icon={Clock}/>
        <Kpi label="Pending amount" value={money(data?.total_pending_usd ?? 0)} icon={WalletIcon} tone="amber"/>
        <Kpi label="As-of" value={new Date().toLocaleTimeString()} icon={RefreshCw}/>
      </div>
      <div className="rounded-2xl bg-slate-50 border border-border px-4 py-3 text-[12.5px] text-foreground/75">
        Wire/ACH topups land here in <span className="mono">pending</span> state. In production, real bank webhooks settle them; admin can manually flip via Settle/Fail with audit reason.
      </div>
      <DataTable loading={loading} rows={data?.rows || []} testid="pending-topups"
        empty="No Allianz topups awaiting settlement."
        columns={[
          { l: "Filed", r: (x) => <span className="text-[11px] text-foreground/55">{new Date(x.created_at).toLocaleString()}</span> },
          { l: "Amount", r: (x) => <span className="display text-[14px] tabular-nums text-emerald-700">+{money(x.amount)}</span> },
          { l: "Method", r: (x) => <span className="text-[11px] mono">{x.method || "—"}</span> },
          { l: "Reference", r: (x) => <span className="mono text-[10.5px] text-foreground/55">{x.ref || "—"}</span> },
          { l: "", r: (x) => (
              <div className="flex justify-end gap-1.5">
                <Button size="sm" data-testid={`topup-settle-${x.id?.slice(0, 6)}`} className="h-8 rounded-full text-[11px]"
                  disabled={busyId === x.id}
                  onClick={() => { setDialog({ mode: "settle", txn_id: x.id }); setReason(""); }}>
                  Settle
                </Button>
                <Button size="sm" variant="outline" data-testid={`topup-fail-${x.id?.slice(0, 6)}`} className="h-8 rounded-full text-[11px]"
                  disabled={busyId === x.id}
                  onClick={() => { setDialog({ mode: "fail", txn_id: x.id }); setReason(""); }}>
                  Fail
                </Button>
              </div>
            ) },
        ]}/>

      {dialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="topup-action-dialog">
          <div className="bg-white rounded-2xl border border-border p-6 w-full max-w-md space-y-3">
            <h3 className="display text-xl">{dialog.mode === "settle" ? "Settle topup" : "Fail topup"}</h3>
            <p className="text-[12.5px] text-foreground/65">
              {dialog.mode === "settle"
                ? "Confirm the bank wire has cleared. The Allianz wallet balance will be credited immediately."
                : "Mark this topup as failed (e.g. wire bounced, NSF). The wallet balance is NOT affected."}
            </p>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-foreground/55">Reason (audit, ≥3 chars)</label>
              <Input data-testid="topup-reason" value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={dialog.mode === "settle" ? "Wire confirmed cleared MT103#..." : "Wire bounced — insufficient funds at source"}/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialog(null)} disabled={busyId === dialog.txn_id}>Cancel</Button>
              <Button data-testid="topup-action-submit" onClick={action} disabled={busyId === dialog.txn_id}>
                <Send className="w-3.5 h-3.5 mr-1"/>{busyId === dialog.txn_id ? "Submitting…" : (dialog.mode === "settle" ? "Settle" : "Mark failed")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
